import type { JourneyMode } from "@/data/schema/compiled";

/** A moment in the story: which scene, and how far into it. */
export interface StoryMoment {
  sceneId: string;
  offsetMs: number;
}

export interface PendingScene {
  journey: JourneyMode;
  moment: StoryMoment | undefined;
}

interface BeatSpan {
  sceneId: string;
  startMs: number;
  endMs: number;
}

export function storyMomentAt(
  beat: BeatSpan | undefined,
  playheadMs: number,
): StoryMoment | undefined {
  return beat === undefined
    ? undefined
    : { sceneId: beat.sceneId, offsetMs: Math.max(0, playheadMs - beat.startMs) };
}

/** Where that moment sits on another rail. Undefined if the scene is not on it. */
export function playheadForMoment(
  beats: readonly BeatSpan[],
  moment: StoryMoment,
): number | undefined {
  const beat = beats.find((item) => item.sceneId === moment.sceneId);
  return beat === undefined
    ? undefined
    : Math.min(beat.startMs + moment.offsetMs, beat.endMs - 1);
}

/** One fetch at a time and one success ever. A failure lets the next call retry. */
export function shareLoad<T>(load: () => Promise<T>): () => Promise<T> {
  let shared: Promise<T> | null = null;
  return () => {
    if (shared === null) {
      shared = load().catch((error: unknown) => {
        shared = null;
        throw error;
      });
    }
    return shared;
  };
}

/** The scene waits for its journey, so a render during loading cannot spend it. */
export function claimPendingScene(
  slot: { current: PendingScene | null },
  journey: JourneyMode,
): { moment: StoryMoment | undefined } | undefined {
  const pending = slot.current;
  if (pending === null || pending.journey !== journey) {
    return undefined;
  }
  slot.current = null;
  return { moment: pending.moment };
}
